<template>
    <div class="space-y-2">
        <div>
            <form-label>GPIO Entity</form-label>
            <GpioEntitySelector :model-value="action.entity" :entities="metadata.entities ?? []"
                @update:model-value="setEntity" />
        </div>

        <div>
            <form-label>Property</form-label>
            <GpioPropertySelector :model-value="action.property" :entity="selectedEntity"
                mode="action" :object-type="node.obj" :event="handler.event"
                @update:model-value="setProperty" />
        </div>

        <div>
            <form-label>Value source</form-label>
            <form-select :model-value="valueSource" @update:model-value="setValueSource">
                <option v-for="source in valueSources" :key="source" :value="source">{{ VALUE_SOURCE_LABELS[source] }}</option>
            </form-select>
        </div>

        <template v-if="valueSource === 'fixed'">
            <form-select v-if="action.property === 'state'" name="Value"
                :model-value="action.fixedValue ? 'on' : 'off'" @update:model-value="setFixedState">
                <option value="on">ON</option>
                <option value="off">OFF</option>
            </form-select>
            <BuilderInput v-else-if="action.property === 'brightness'" name="Value" type="number"
                :min="propertyRange.min" :max="propertyRange.max" :model-value="action.fixedValue"
                @update:model-value="setFixedBrightness" />
            <div v-else-if="action.property === 'rgb_color'" class="flex items-center gap-2">
                <form-label class="w-2/5 shrink-0">Colour</form-label>
                <ColorPicker :pureColor="action.fixedValue" useType="pure" format="hex" lang="En" shape="circle"
                    @pureColorChange="setFixedColor" />
            </div>
        </template>

        <template v-else-if="valueSource === 'event'">
            <div class="rounded border border-slate-600/60 bg-slate-900/20 px-2 py-1.5 text-[11px] text-slate-400">
                Source: <span class="text-slate-200">{{ sourceLabel }}</span>
            </div>
            <div v-if="action.property === 'brightness'" class="space-y-1">
                <div class="text-[11px] font-semibold uppercase tracking-wide text-slate-400">Mapping</div>
                <div class="grid grid-cols-2 gap-2">
                    <BuilderInput name="Input min" type="number" :model-value="action.inMin"
                        @update:model-value="setRange('inMin', $event)" />
                    <BuilderInput name="Input max" type="number" :model-value="action.inMax"
                        @update:model-value="setRange('inMax', $event)" />
                    <BuilderInput name="GPIO min" type="number" :model-value="action.outMin"
                        @update:model-value="setRange('outMin', $event)" />
                    <BuilderInput name="GPIO max" type="number" :model-value="action.outMax"
                        @update:model-value="setRange('outMax', $event)" />
                </div>
            </div>
        </template>

        <p v-if="!metadata.status?.available" class="text-[11px] text-amber-300">
            Plate metadata is unavailable; manual entity IDs remain supported and are validated by the firmware at runtime.
        </p>
        <p v-else-if="metadata.status?.cached" class="text-[11px] text-amber-300">
            Using cached/offline GPIO metadata for this plate.
        </p>
        <p class="text-[11px] text-slate-500">
            <template v-if="form === 'binding'">Exported as a firmware GPIO binding of this object.</template>
            <template v-else>Exported as the command <code class="break-all">{{ command || 'gpio/entity/…' }}</code>.</template>
        </p>
        <p v-if="firmwareFeatures.length" class="text-[11px] text-amber-300">
            Needs newer firmware: {{ firmwareFeatures.join(', ') }}.
        </p>
    </div>
</template>

<script setup>
import { computed } from 'vue'
import { ColorPicker } from 'vue3-colorpicker'
import 'vue3-colorpicker/style.css'
import BuilderInput from '../../hassbuilder/automation/BuilderInput.vue'
import GpioEntitySelector from '../../gpio/GpioEntitySelector.vue'
import GpioPropertySelector from '../../gpio/GpioPropertySelector.vue'
import { eventPropertyOptions, rangeForObject, rangeForProperty } from '../../../gpio/gpioEditorModel.js'
import {
    gpioActionValueSources,
    gpioCommandFirmwareFeatures,
    gpioEntityActionForm,
    gpioEntityActionToCommand,
} from '../../../actions/gpioEntityAction.js'

const VALUE_SOURCE_LABELS = { event: 'Event value / object property', fixed: 'Fixed value', toggle: 'Toggle' }

const props = defineProps({
    node: { type: Object, required: true },
    handler: { type: Object, required: true },
    action: { type: Object, required: true },
    settings: { type: Object, default: () => ({}) },
    metadata: { type: Object, default: () => ({ entities: [], status: {} }) },
    // Conditional (behind a condition or switch): always an entity command.
    nested: { type: Boolean, default: false },
})
const emit = defineEmits(['change'])

const selectedEntity = computed(() => (props.metadata.entities ?? []).find(entity => entity.id === props.action.entity) ?? null)
const propertyRange = computed(() => rangeForProperty(selectedEntity.value, props.action.property))
const valueSource = computed(() => props.action.valueSource ?? 'event')
const valueSources = computed(() => {
    const sources = gpioActionValueSources(props.action.property, { event: props.handler.event, node: props.node })
    return sources.includes(valueSource.value) ? sources : [valueSource.value, ...sources]
})
const sourceLabel = computed(() => {
    if (props.action.property === 'rgb_color') return 'Event colour ({r}, {g}, {b})'
    return props.action.sourceProperty === 'state'
        ? 'Event state (ON/OFF semantics)'
        : 'Event value from the selected UI object'
})
const form = computed(() => gpioEntityActionForm(props.action, { event: props.handler.event, node: props.node, nested: props.nested }))
const command = computed(() => gpioEntityActionToCommand(props.action))
const firmwareFeatures = computed(() => (form.value === 'command' ? gpioCommandFirmwareFeatures(command.value) : []))

const sourcePropertyFor = property => (property === 'rgb_color' ? 'color' : property === 'brightness' ? 'val' : 'state')

function ensureRanges(property = props.action.property, sourceProperty = props.action.sourceProperty) {
    const ranges = sourceProperty === 'state'
        ? { min: 0, max: 1 }
        : rangeForObject(props.node)
    const output = rangeForProperty(selectedEntity.value, property)
    props.action.inMin = ranges.min
    props.action.inMax = ranges.max
    props.action.outMin = output.min
    props.action.outMax = output.max
}

function setEntity(value) {
    props.action.entity = value
    ensureRanges()
    emit('change')
}

function setProperty(value) {
    props.action.property = value
    props.action.sourceProperty = sourcePropertyFor(value)
    props.action.fixedValue = value === 'state' ? true : value === 'rgb_color' ? '#ffffff' : propertyRange.value.min
    if (!gpioActionValueSources(value, { event: props.handler.event, node: props.node }).includes(valueSource.value)) {
        props.action.valueSource = 'fixed'
    }
    ensureRanges(value, props.action.sourceProperty)
    emit('change')
}

function setValueSource(value) {
    props.action.valueSource = value
    if (value === 'event') {
        props.action.sourceProperty = sourcePropertyFor(props.action.property)
        ensureRanges()
    } else if (value === 'fixed' && props.action.property === 'brightness' && !Number.isFinite(Number(props.action.fixedValue))) {
        props.action.fixedValue = propertyRange.value.min
    }
    emit('change')
}

function setFixedState(value) {
    props.action.fixedValue = value === 'on'
    emit('change')
}

function setFixedBrightness(value) {
    props.action.fixedValue = Number(value)
    emit('change')
}

function setFixedColor(value) {
    props.action.fixedValue = value
    emit('change')
}

function setRange(key, value) {
    props.action[key] = Number(value)
    emit('change')
}

// Keep legacy actions with a missing source initialized when they first render.
if (!props.action.sourceProperty) {
    props.action.sourceProperty = props.action.property === 'rgb_color' ? 'color'
        : eventPropertyOptions(props.node.obj, props.handler.event).includes('brightness') ? 'val' : 'state'
}
</script>
